import { Link } from "react-router-dom";
import { useTranslation } from "@/context/TranslationContext";
import { useState, useEffect } from "react";

const convertDriveLink = (url: string): string => {
  if (!url) return "";
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w800`;
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w800`;
  return url;
};

function extractDriveId(url: string): string | null {
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return fileMatch[1];
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return idMatch[1];
  return null;
}

function ServiceImage({
  url,
  alt,
  className,
}: {
  url?: string;
  alt: string;
  className?: string;
}) {
  const [src, setSrc] = useState(() => convertDriveLink(url || ""));
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setSrc(convertDriveLink(url || ""));
    setFallbackUsed(false);
    setErrored(false);
  }, [url]);

  const handleError = () => {
    if (!fallbackUsed && url) {
      const id = extractDriveId(url);
      if (id) {
        setFallbackUsed(true);
        setSrc(`https://drive.google.com/thumbnail?id=${id}&sz=w800`);
        return;
      }
    }
    setErrored(true);
  };

  if (!src || errored) {
    return (
      <div
        className={className}
        style={{
          background: "linear-gradient(135deg,#0C1528 0%,#060D1A 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ri-image-line" style={{ fontSize: 36, opacity: 0.15, color: "#00E5FF" }} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      referrerPolicy="no-referrer"
      className={className}
      onError={handleError}
    />
  );
}

const services = [
  {
    icon: "ri-shield-keyhole-line",
    titleKey: "home_services.service1_title",
    descKey: "home_services.service1_desc",
    href: "/services#security",
  },
  {
    icon: "ri-code-s-slash-line",
    titleKey: "home_services.service2_title",
    descKey: "home_services.service2_desc",
    href: "/services#engineering",
  },
  {
    icon: "ri-line-chart-line",
    titleKey: "home_services.service3_title",
    descKey: "home_services.service3_desc",
    href: "/services#fintech",
  },
  {
    icon: "ri-palette-line",
    titleKey: "home_services.service4_title",
    descKey: "home_services.service4_desc",
    href: "/services#design",
  },
  {
    icon: "ri-brain-line",
    titleKey: "home_services.service5_title",
    descKey: "home_services.service5_desc",
    href: "/services#ai",
  },
  {
    icon: "ri-cloud-line",
    titleKey: "home_services.service6_title",
    descKey: "home_services.service6_desc",
    href: "/services#devops",
  },
  {
    icon: "ri-brush-line",
    titleKey: "home_services.service7_title",
    descKey: "home_services.service7_desc",
    href: "/services#branding",
  },
  {
    icon: "ri-image-edit-line",
    titleKey: "home_services.service8_title",
    descKey: "home_services.service8_desc",
    href: "/services#flyers",
  },
  {
    icon: "ri-cpu-line",
    titleKey: "home_services.service9_title",
    descKey: "home_services.service9_desc",
    href: "/services#hardware",
  },
  {
    icon: "ri-customer-service-2-line",
    titleKey: "home_services.service10_title",
    descKey: "home_services.service10_desc",
    href: "/services#it-support",
  },
  {
    icon: "ri-server-line",
    titleKey: "home_services.service11_title",
    descKey: "home_services.service11_desc",
    href: "/services#hosting",
  },
  {
    icon: "ri-wifi-line",
    titleKey: "home_services.service12_title",
    descKey: "home_services.service12_desc",
    href: "/services#networking",
  },
];

export default function ServicesSection() {
  const { t } = useTranslation();
  const servicesImageUrl = "https://drive.google.com/file/d/1OGdm7W_8VYeAK6eDaIZ5CEBBWkmR8KTa/view?usp=sharing";
  return (
    <section id="services" className="bg-dark-panel py-28 px-4 md:px-12 lg:px-20 relative overflow-hidden">
      {/* Top cyan circuit border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue/25 to-transparent" />

      {/* Background dot pattern */}
      <div className="absolute inset-0 dot-pattern opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-14 lg:gap-20 items-start">

          {/* ── Left block ── */}
          <div className="lg:sticky lg:top-28">
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("home_services.badge")}
            </span>
            <h2
              className="font-black text-white leading-tight mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(1.9rem, 3.5vw, 3rem)" }}
            >
              {t("home_services.title")} <span className="text-electric-blue">{t("home_services.highlight")}</span>{t("home_services.title_rest")}
            </h2>
            <p className="text-silver text-base leading-relaxed mb-8 max-w-sm">
              {t("home_services.description")}
            </p>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-electric-blue text-sm font-semibold hover:gap-3 transition-all duration-200 cursor-pointer group"
            >
              {t("home_services.explore_all")}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </Link>

            {/* Picture div below text/link */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-dark-border">
              <ServiceImage
                url={servicesImageUrl}
                alt="Services"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* ── Right block — 2×3 card grid ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {services.map((service) => (
              <Link
                key={service.titleKey}
                to={service.href}
                className="group relative rounded-[14px] p-7 flex flex-col gap-4 transition-all duration-250 cursor-pointer"
                style={{
                  background: "var(--color-bg-card)",
                  border: "1px solid rgba(0, 229, 255, 0.12)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = "0 0 20px rgba(0,229,255,0.15)";
                  (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)";
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(0,229,255,0.28)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = "none";
                  (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(0,229,255,0.12)";
                }}
              >
                {/* Icon container */}
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-[10px] flex-shrink-0"
                  style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.15)" }}
                >
                  <i className={`${service.icon} text-electric-blue text-xl`} />
                </div>

                {/* Text */}
                <div>
                  <h3
                    className="text-white font-semibold text-[1.05rem] mb-2 group-hover:text-electric-blue transition-colors duration-200"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-silver text-sm leading-relaxed">
                    {t(service.descKey)}
                  </p>
                </div>

                {/* Learn More */}
                <span className="text-electric-blue text-xs font-semibold mt-auto flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                  {t("home_services.learn_more")} <i className="ri-arrow-right-line" />
                </span>

                {/* Circuit corner decoration */}
                <svg
                  className="absolute bottom-2 right-2 w-8 h-8 text-electric-blue opacity-[0.08]"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path d="M32 0 L32 12 L24 12 L24 20 L16 20 L16 32" stroke="currentColor" strokeWidth="1" />
                  <circle cx="32" cy="0" r="2" fill="currentColor" />
                  <circle cx="24" cy="12" r="1.5" fill="currentColor" />
                  <circle cx="16" cy="32" r="2" fill="currentColor" />
                </svg>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom cyan border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue/25 to-transparent" />
    </section>
  );
}
